/**
 * CampaignPageEditor - CMS-style editor for campaign pages
 * Allows admins to customize sections, content, and layout
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CampaignRecurrenceCard from '@/components/CampaignRecurrenceCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Save, Eye, ArrowLeft, Plus, Trash2, GripVertical, Image, FileText,
  Target, Heart, Users, Phone, Mail, MapPin, Loader2, Upload, X,
  ChevronUp, ChevronDown, Settings, Palette, Layout, Type
} from 'lucide-react';
import api from '@/lib/api';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { FileUpload } from '@/components/ui/file-upload';
import { SECTION_TYPES, API_URL } from './campaign/CampaignConstants';
import { MilestonesEditor } from './campaign/CampaignMilestones';
import { GalleryEditor } from './campaign/CampaignGallery';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Section Item Component
const SortableSectionItem = ({ section, onToggle, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionType = SECTION_TYPES.find(t => t.id === section.id);
  const Icon = sectionType?.icon || Layout;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        section.enabled ? 'bg-white' : 'bg-slate-50 opacity-60'
      } ${isDragging ? 'shadow-lg ring-2 ring-emerald-500' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="h-4 w-4 text-slate-400" />
      </div>
      <div className="flex items-center gap-2 flex-1">
        <Icon className="h-4 w-4 text-slate-600" />
        <span className="font-medium">{sectionType?.name || section.id}</span>
        <span className="text-xs text-slate-500 hidden sm:inline">{sectionType?.description}</span>
      </div>
      <div className="flex items-center gap-2">
        <Switch 
          checked={section.enabled}
          onCheckedChange={() => onToggle(section.id)}
        />
        <Button 
          variant="ghost" 
          size="sm"
          className="text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onRemove(section.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

const CampaignPageEditor = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [searchParams] = useSearchParams();
  const initialTab = ['content', 'layout', 'theme', 'milestones', 'gallery', 'recurrence'].includes(searchParams.get('tab'))
    ? searchParams.get('tab') : 'content';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Page layout state
  const [pageConfig, setPageConfig] = useState({
    sections: [
      { id: 'hero', enabled: true, order: 1 },
      { id: 'stats', enabled: true, order: 2 },
      { id: 'mission', enabled: true, order: 3 },
      { id: 'milestones', enabled: true, order: 4 },
      { id: 'updates', enabled: true, order: 5 },
      { id: 'quicklinks', enabled: true, order: 6 },
      { id: 'cta', enabled: true, order: 7 },
      { id: 'fundraising', enabled: true, order: 8 },
      { id: 'newsletter', enabled: true, order: 9 },
    ],
    theme: {
      primaryColor: '#059669', // emerald-600
      accentColor: '#14b8a6', // teal-500
      darkMode: false,
    },
    customCss: '',
  });

  // Campaign data
  const [campaignData, setCampaignData] = useState({
    name: '',
    slug: '',
    tagline: '',
    hero_kicker: '',
    description: '',
    mission_statement: '',
    target_amount: 0,
    hero_image: '',
    logo: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
  });

  // Fetch campaign
  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign-edit', campaignId],
    queryFn: async () => {
      const res = await api.get(`/campaigns/admin/list`);
      return res.data.find(c => c.id === campaignId);
    },
    enabled: !!campaignId
  });

  // Load campaign data
  useEffect(() => {
    if (campaign) {
      setCampaignData({
        name: campaign.name || '',
        slug: campaign.slug || '',
        tagline: campaign.tagline || '',
        hero_kicker: campaign.hero_kicker || '',
        description: campaign.description || '',
        mission_statement: campaign.mission_statement || '',
        target_amount: campaign.target_amount || 0,
        hero_image: campaign.hero_image || '',
        logo: campaign.logo || '',
        contact_email: campaign.contact_email || '',
        contact_phone: campaign.contact_phone || '',
        contact_address: campaign.contact_address || '',
      });
      
      // Load saved page config if exists
      if (campaign.page_config) {
        setPageConfig(campaign.page_config);
      }
    }
  }, [campaign]);

  // Save campaign
  const saveMutation = useMutation({
    mutationFn: async () => {
      const formData = new FormData();
      Object.entries(campaignData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      // Save page config as JSON string
      formData.append('page_config', JSON.stringify(pageConfig));

      return api.put(`/campaigns/admin/${campaignId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      toast.success('Campaign saved!');
      queryClient.invalidateQueries({ queryKey: ['campaign-edit', campaignId] });
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join('; ') : (e?.message || 'Failed to save');
      toast.error(msg);
    }
  });

  // Toggle section
  const toggleSection = (sectionId) => {
    setPageConfig(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, enabled: !s.enabled } : s
      )
    }));
  };

  // Move section
  const moveSection = (sectionId, direction) => {
    setPageConfig(prev => {
      const sections = [...prev.sections];
      const index = sections.findIndex(s => s.id === sectionId);
      if (index < 0) return prev;
      
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= sections.length) return prev;
      
      const temp = sections[index].order;
      sections[index].order = sections[newIndex].order;
      sections[newIndex].order = temp;
      
      return { ...prev, sections: sections.sort((a, b) => a.order - b.order) };
    });
  };

  // Add section
  const addSection = (sectionType) => {
    setPageConfig(prev => {
      const maxOrder = Math.max(...prev.sections.map(s => s.order), 0);
      return {
        ...prev,
        sections: [...prev.sections, { id: sectionType, enabled: true, order: maxOrder + 1 }]
      };
    });
    setShowAddSection(false);
  };

  // Remove section
  const removeSection = (sectionId) => {
    setPageConfig(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for reordering
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setPageConfig(prev => {
        const sections = [...prev.sections].sort((a, b) => a.order - b.order);
        const oldIndex = sections.findIndex(s => s.id === active.id);
        const newIndex = sections.findIndex(s => s.id === over.id);
        
        const reordered = arrayMove(sections, oldIndex, newIndex);
        // Update order values
        const withNewOrder = reordered.map((s, i) => ({ ...s, order: i + 1 }));
        
        return { ...prev, sections: withNewOrder };
      });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Skeleton className="h-12 w-64 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/campaigns')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-lg font-semibold">{campaignData.name || 'Campaign Editor'}</h1>
              <p className="text-xs text-slate-500">/{campaignData.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={`/campaigns/${campaignData.slug}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <Eye className="h-4 w-4 mr-1" />
                Preview
              </Button>
            </a>
            <Button 
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="sm"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Save className="h-4 w-4 mr-1" />
              )}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="content">
              <Type className="h-4 w-4 mr-2" />
              Content
            </TabsTrigger>
            <TabsTrigger value="layout">
              <Layout className="h-4 w-4 mr-2" />
              Page Layout
            </TabsTrigger>
            <TabsTrigger value="theme">
              <Palette className="h-4 w-4 mr-2" />
              Theme
            </TabsTrigger>
            <TabsTrigger value="milestones">
              <Target className="h-4 w-4 mr-2" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="gallery">
              <Image className="h-4 w-4 mr-2" />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="recurrence" data-testid="campaign-tab-recurrence">
              Recurrence
            </TabsTrigger>
          </TabsList>

          {/* Content Tab */}
          <TabsContent value="content">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Campaign Name</Label>
                    <Input
                      value={campaignData.name}
                      onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Hero Kicker</Label>
                    <Input
                      value={campaignData.hero_kicker || ''}
                      onChange={(e) => setCampaignData({ ...campaignData, hero_kicker: e.target.value })}
                      placeholder='e.g. "Building a Healthier Future" — short uppercase line above the title'
                      data-testid="campaign-hero-kicker-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Leave blank to hide.</p>
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={campaignData.tagline}
                      onChange={(e) => setCampaignData({ ...campaignData, tagline: e.target.value })}
                      placeholder="Short description for hero"
                    />
                  </div>
                  <div>
                    <Label>Hero Image / Video</Label>
                    <FileUpload
                      value={campaignData.hero_image}
                      onChange={(url) => setCampaignData({ ...campaignData, hero_image: url })}
                      accept="image/*,video/*"
                      placeholder="Upload hero image or video (max 50MB image · 200MB video)"
                      previewHeight="180px"
                      fileType="hero"
                      maxSize={200}
                      uploadEndpoint="/api/campaigns/admin/upload"
                    />
                  </div>
                  <div>
                    <Label>Fundraising Goal ($)</Label>
                    <Input
                      type="number"
                      value={campaignData.target_amount}
                      onChange={(e) => setCampaignData({ ...campaignData, target_amount: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label>Amount Raised ($)</Label>
                    <Input
                      type="number"
                      value={campaignData.raised_amount || 0}
                      onChange={(e) => setCampaignData({ ...campaignData, raised_amount: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Overall Progress (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={campaignData.overall_progress || 0}
                      onChange={(e) => setCampaignData({ ...campaignData, overall_progress: parseInt(e.target.value) || 0 })}
                      placeholder="0"
                    />
                    <p className="text-xs text-slate-500 mt-1">Manual progress override (0-100%)</p>
                  </div>
                  <div>
                    <Label>Progress Display Mode</Label>
                    <select
                      className="w-full mt-1 p-2 border rounded-md bg-white"
                      value={campaignData.progress_display_mode || 'both'}
                      onChange={(e) => setCampaignData({ ...campaignData, progress_display_mode: e.target.value })}
                    >
                      <option value="percentage">Percentage Only (e.g., 75%)</option>
                      <option value="figures">Figures Only (e.g., $75,000 / $100,000)</option>
                      <option value="both">Both Percentage & Figures</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">How progress is shown on the campaign page</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Mission & Description</CardTitle>
                  <CardDescription>Use the rich text editor for formatted content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mission Statement</Label>
                    <RichTextEditor
                      value={campaignData.mission_statement}
                      onChange={(html) => setCampaignData({ ...campaignData, mission_statement: html })}
                      placeholder="Your organization's mission..."
                      minHeight="180px"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <RichTextEditor
                      value={campaignData.description}
                      onChange={(html) => setCampaignData({ ...campaignData, description: html })}
                      placeholder="Additional details about your campaign..."
                      minHeight="150px"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={campaignData.contact_email}
                        onChange={(e) => setCampaignData({ ...campaignData, contact_email: e.target.value })}
                        placeholder="info@example.org"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={campaignData.contact_phone}
                        onChange={(e) => setCampaignData({ ...campaignData, contact_phone: e.target.value })}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={campaignData.contact_address}
                        onChange={(e) => setCampaignData({ ...campaignData, contact_address: e.target.value })}
                        placeholder="City, Country"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Layout Tab */}
          <TabsContent value="layout">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Page Sections</CardTitle>
                  <CardDescription>Drag sections to reorder, toggle to show/hide</CardDescription>
                </div>
                <Button size="sm" onClick={() => setShowAddSection(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Section
                </Button>
              </CardHeader>
              <CardContent>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={pageConfig.sections.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {pageConfig.sections.sort((a, b) => a.order - b.order).map((section) => (
                        <SortableSectionItem
                          key={section.id}
                          section={section}
                          onToggle={toggleSection}
                          onRemove={removeSection}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <p className="text-xs text-slate-500 mt-4 flex items-center gap-2">
                  <GripVertical className="h-3 w-3" />
                  Drag sections to reorder them on your campaign page
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Theme Tab */}
          <TabsContent value="theme">
            <Card>
              <CardHeader>
                <CardTitle>Color Theme</CardTitle>
                <CardDescription>Customize the colors of your campaign page</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex gap-2 mt-2">
                      <input
                        type="color"
                        value={pageConfig.theme.primaryColor}
                        onChange={(e) => setPageConfig({
                          ...pageConfig,
                          theme: { ...pageConfig.theme, primaryColor: e.target.value }
                        })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={pageConfig.theme.primaryColor}
                        onChange={(e) => setPageConfig({
                          ...pageConfig,
                          theme: { ...pageConfig.theme, primaryColor: e.target.value }
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Accent Color</Label>
                    <div className="flex gap-2 mt-2">
                      <input
                        type="color"
                        value={pageConfig.theme.accentColor}
                        onChange={(e) => setPageConfig({
                          ...pageConfig,
                          theme: { ...pageConfig.theme, accentColor: e.target.value }
                        })}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={pageConfig.theme.accentColor}
                        onChange={(e) => setPageConfig({
                          ...pageConfig,
                          theme: { ...pageConfig.theme, accentColor: e.target.value }
                        })}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Color Presets</Label>
                  <div className="flex gap-2 mt-2">
                    {[
                      { primary: '#059669', accent: '#14b8a6', name: 'Emerald' },
                      { primary: '#2563eb', accent: '#3b82f6', name: 'Blue' },
                      { primary: '#7c3aed', accent: '#8b5cf6', name: 'Purple' },
                      { primary: '#dc2626', accent: '#ef4444', name: 'Red' },
                      { primary: '#ea580c', accent: '#f97316', name: 'Orange' },
                    ].map(preset => (
                      <Button
                        key={preset.name}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                        onClick={() => setPageConfig({
                          ...pageConfig,
                          theme: { ...pageConfig.theme, primaryColor: preset.primary, accentColor: preset.accent }
                        })}
                      >
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Custom CSS (Advanced)</Label>
                  <Textarea
                    value={pageConfig.customCss}
                    onChange={(e) => setPageConfig({ ...pageConfig, customCss: e.target.value })}
                    rows={5}
                    placeholder="/* Custom styles */"
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Milestones Tab */}
          <TabsContent value="milestones">
            <MilestonesEditor campaignId={campaignId} />
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <GalleryEditor campaignId={campaignId} slug={campaignData.slug} />
          </TabsContent>

          {/* Recurrence Tab */}
          <TabsContent value="recurrence">
            <CampaignRecurrenceCard campaignId={campaignId} existing={campaignData} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Section Dialog */}
      <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {SECTION_TYPES.map(type => (
              <Button
                key={type.id}
                variant="outline"
                className="h-auto p-4 flex flex-col items-start text-left"
                onClick={() => addSection(type.id)}
              >
                <type.icon className="h-5 w-5 mb-2 text-emerald-600" />
                <span className="font-medium">{type.name}</span>
                <span className="text-xs text-slate-500">{type.description}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignPageEditor;

